import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Search, X } from 'lucide-react';
import { formatPhoneForDisplay, } from '@super/shared-types';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { TransferActionForm, emptyTransferParameters, } from './TransferActionForm';
import { actionTypeIcon, BasicInfoFields, duplicatedName, mentionIcon, ReferenceEditShell, ReferencePanel, SpineList, validateName, validateTransferParameters, } from './shared';
/**
 * Inspector for a Transfer node. The node drops on the canvas with an empty
 * `workflowActionId`; the inspector opens showing the inline picker (search +
 * list + create). Once the author picks or creates, the inspector switches to
 * the configured view with a "Change" button that reopens the picker.
 *
 * A Transfer node holds exactly one transfer action. No group-member
 * management lives here; adding a non-TRANSFER action to a group is blocked
 * in the admin's group editor.
 */
export function TransferNodePanel({ workflowActionId, reference, options, onPickExisting, onCreateInline, onFetchUsageCount, onDuplicate, onSaveParameters, onRefresh, onDirtyChange, onEditingValidateChange, onNavigateToCatalog, renderEditForm, describeRecipient, }) {
    return (_jsx(ReferencePanel, { headerLabel: "Transfer action", decidingNoun: "transfer action", decidingNounPlural: "transfers", missingLabel: "transfer", referenceId: workflowActionId, reference: reference, renderPicker: ({ onPickReference, onStartDraft }) => (_jsx(TransferPicker, { options: options, onPickExisting: onPickReference, onCreateEmpty: onStartDraft })), getSummaryTitle: ref => {
            const name = ref.name ?? 'Untitled transfer';
            const count = ref.parameters?.recipients.length ?? 0;
            const label = count === 1 ? 'recipient' : 'recipients';
            return `${name} • ${count} ${label}`;
        }, renderSummaryBody: ref => (_jsx(TransferSummaryBody, { reference: ref, describeRecipient: describeRecipient })), renderEditBody: ctx => (_jsx(TransferEditBody, { ctx: ctx, onPickExisting: onPickExisting, onCreateInline: onCreateInline, onDuplicate: onDuplicate, onSaveParameters: onSaveParameters, onDirtyChange: onDirtyChange, onEditingValidateChange: onEditingValidateChange, renderEditForm: renderEditForm })), onReplaceReference: onPickExisting, onNavigateToCatalog: onNavigateToCatalog ? ref => onNavigateToCatalog(ref) : undefined, onRefresh: onRefresh, onFetchUsageCount: onFetchUsageCount, allowDraftDuplicate: true }));
}
function TransferSummaryBody({ reference, describeRecipient, }) {
    const params = reference.parameters;
    const method = params?.connectionMethod === 'AI_TRANSFER'
        ? 'AI phone tree'
        : 'Simple transfer';
    const recipients = params?.recipients ?? [];
    const items = [];
    let lastDedupKey = null;
    recipients.forEach((r, i) => {
        const resolved = describeRecipient?.(r) ?? null;
        const dedupKey = resolved?.key ?? null;
        if (dedupKey && dedupKey === lastDedupKey)
            return;
        lastDedupKey = dedupKey;
        const label = resolved?.label ??
            formatPhoneForDisplay(r.extension ? `${r.number}#${r.extension}` : r.number);
        const Icon = resolved?.mention ? mentionIcon : actionTypeIcon('TRANSFER');
        items.push({
            key: `${r.number}:${r.extension ?? ''}:${i}`,
            Icon,
            label,
        });
    });
    return (_jsxs("div", { className: "space-y-0", children: [_jsx("p", { className: "text-muted-foreground truncate", children: method }), items.length === 0 ? (_jsx("p", { className: "text-muted-foreground", children: "No recipients set" })) : (_jsx(SpineList, { items: items }))] }));
}
function TransferEditBody({ ctx, onPickExisting, onCreateInline, onDuplicate, onSaveParameters, onDirtyChange, onEditingValidateChange, renderEditForm, }) {
    const [parameters, setParameters] = useState(() => ctx.reference?.parameters ?? emptyTransferParameters());
    const [name, setName] = useState(() => {
        const base = ctx.reference?.name ?? '';
        if (!ctx.isDuplicate)
            return base;
        return duplicatedName(base) ?? base;
    });
    const [description, setDescription] = useState(ctx.reference?.description ?? '');
    const [nameError, setNameError] = useState(false);
    const [recipientsError, setRecipientsError] = useState(false);
    const [dirty, setDirty] = useState(ctx.isDuplicate);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const nameInputRef = useRef(null);
    const recipientsSectionRef = useRef(null);
    // Signal "node has unsaved edits" to the admin graph-save gate for the
    // whole lifetime of the edit sub-view.
    const dirtyChangeRef = useRef(onDirtyChange);
    useEffect(() => {
        dirtyChangeRef.current = onDirtyChange;
    }, [onDirtyChange]);
    useEffect(() => {
        if (!ctx.referenceId)
            return;
        dirtyChangeRef.current?.(ctx.referenceId, true);
        return () => {
            dirtyChangeRef.current?.(ctx.referenceId, false);
        };
    }, [ctx.referenceId]);
    const cta = ctx.isDraft
        ? 'Create transfer'
        : ctx.isDuplicate
            ? 'Save transfer'
            : 'Update transfer';
    const draft = () => ({
        name: name.trim() ? name.trim() : null,
        description: description.trim() ? description.trim() : null,
        parameters,
    });
    const handleParametersChange = (next) => {
        setDirty(true);
        setParameters(next);
        // Clear the recipients red state as soon as the author adds a recipient
        // with a non-empty number — same auto-clear pattern as nameError.
        if (recipientsError &&
            next.recipients.length > 0 &&
            next.recipients.every(r => r.number.trim())) {
            setRecipientsError(false);
        }
    };
    const validate = () => {
        const issues = [];
        const nameResult = validateName(name);
        setNameError(nameResult.nameError);
        if (nameResult.issue)
            issues.push(nameResult.issue);
        const paramsResult = validateTransferParameters(parameters);
        const recipientsInvalid = paramsResult.issue != null;
        setRecipientsError(recipientsInvalid);
        if (paramsResult.issue)
            issues.push(paramsResult.issue);
        return {
            issues,
            nameError: nameResult.nameError,
            recipientsError: recipientsInvalid,
        };
    };
    const focusFirstInvalid = (nameInvalid) => {
        if (nameInvalid) {
            nameInputRef.current?.focus();
            return;
        }
        const section = recipientsSectionRef.current;
        section?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        section
            ?.querySelector('input,textarea')
            ?.focus();
    };
    // Expose the validator to the graph-save gate: on mount register, on
    // unmount clear. Matches the Action inspector's lifted `validate`.
    const validateHandlerRef = useRef(() => true);
    useEffect(() => {
        validateHandlerRef.current = () => {
            const { issues, nameError: nameInvalid } = validate();
            if (issues.length > 0) {
                setError(issues.join(' · '));
                focusFirstInvalid(nameInvalid);
                return false;
            }
            return true;
        };
    });
    const editingValidateChangeRef = useRef(onEditingValidateChange);
    useEffect(() => {
        editingValidateChangeRef.current = onEditingValidateChange;
    }, [onEditingValidateChange]);
    useEffect(() => {
        const stableValidate = () => validateHandlerRef.current();
        editingValidateChangeRef.current?.(stableValidate);
        return () => editingValidateChangeRef.current?.(null);
    }, []);
    const handleSubmit = async () => {
        setError(null);
        const { issues, nameError: nameInvalid } = validate();
        if (issues.length > 0) {
            setError(issues.join(' · '));
            focusFirstInvalid(nameInvalid);
            return;
        }
        setSaving(true);
        if (ctx.isDraft) {
            const newId = await onCreateInline(parameters);
            setSaving(false);
            if (!newId) {
                setError('Could not create the transfer. Try again.');
                return;
            }
            onPickExisting(newId);
            return;
        }
        if (ctx.isDuplicate) {
            const newId = await onDuplicate(ctx.referenceId, draft());
            setSaving(false);
            if (!newId) {
                setError('Could not duplicate the transfer. Try again.');
                return;
            }
            onPickExisting(newId);
            return;
        }
        const ok = await onSaveParameters(ctx.referenceId, draft());
        setSaving(false);
        if (!ok) {
            setError('Could not save the transfer. Try again.');
            return;
        }
        ctx.onDone();
    };
    return (_jsxs(ReferenceEditShell, { onBack: ctx.onDone, onBeforeBack: () => !dirty ||
            window.confirm('You have unsaved changes. Discard them and go back?'), backDisabled: saving, bodyPadding: "p-4", footer: _jsx(Button, { size: "sm", onClick: handleSubmit, disabled: saving, children: saving ? 'Saving…' : cta }), children: [_jsxs("div", { className: "space-y-4", children: [_jsx(BasicInfoFields, { ref: nameInputRef, name: name, description: description, onNameChange: next => {
                            setDirty(true);
                            setName(next);
                            if (next.trim())
                                setNameError(false);
                        }, onDescriptionChange: next => {
                            setDirty(true);
                            setDescription(next);
                        }, nameError: nameError, namePlaceholder: "Transfer to leasing", autoFocusName: true }), _jsx("div", { ref: recipientsSectionRef, children: renderEditForm ? (renderEditForm({
                            value: parameters,
                            onChange: handleParametersChange,
                            errors: { recipientsError },
                        })) : (_jsx(TransferActionForm, { value: parameters, onChange: handleParametersChange })) })] }), error && _jsx("p", { className: "ml-3 pt-2 text-xs text-destructive", children: error })] }));
}
function TransferPicker({ options, onPickExisting, onCreateEmpty, }) {
    const [query, setQuery] = useState('');
    const filtered = useMemo(() => {
        const needle = query.trim().toLowerCase();
        const named = options.filter(o => o.name != null);
        if (!needle)
            return named;
        return named.filter(o => o.searchHaystack.toLowerCase().includes(needle));
    }, [query, options]);
    const actions = filtered.filter(o => o.source === 'action');
    const groups = filtered.filter(o => o.source === 'group');
    return (_jsxs("div", { className: "space-y-3", children: [_jsxs("div", { className: "relative", children: [_jsx(Search, { className: "pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" }), _jsx(Input, { value: query, onChange: e => setQuery(e.target.value), placeholder: "Search actions and action groups", className: "h-8 pl-7 pr-7 text-xs", autoFocus: true }), query && (_jsx(Button, { type: "button", variant: "ghost", size: "icon", className: "absolute right-0.5 top-1/2 h-7 w-7 -translate-y-1/2 text-muted-foreground", onClick: () => setQuery(''), children: _jsx(X, { className: "h-3 w-3" }) }))] }), options.filter(o => o.name != null).length === 0 ? (_jsx("p", { className: "text-xs text-muted-foreground", children: "No shared transfers in the catalog yet. Create one for this flow and it becomes available to every graph in this company." })) : filtered.length === 0 ? (_jsxs("p", { className: "text-xs text-muted-foreground", children: ["No matches for \"", query, "\"."] })) : (_jsxs("div", { className: "space-y-3 max-h-80 overflow-y-auto", children: [actions.length > 0 && (_jsx(OptionGroup, { label: "Actions", rows: actions, onPick: onPickExisting })), groups.length > 0 && (_jsx(OptionGroup, { label: "Action groups", rows: groups, onPick: onPickExisting }))] })), _jsx("div", { className: "flex items-center justify-end", children: _jsxs(Button, { type: "button", variant: "outline", size: "sm", className: "h-8 text-xs", onClick: onCreateEmpty, children: [_jsx(Plus, { className: "h-3 w-3 mr-1" }), "Create new transfer"] }) })] }));
}
function OptionGroup({ label, rows, onPick, }) {
    return (_jsxs("div", { className: "space-y-1", children: [_jsx("p", { className: "text-[10px] uppercase tracking-wide text-muted-foreground", children: label }), _jsx("div", { className: "space-y-1", children: rows.map(opt => (_jsxs("button", { type: "button", onClick: () => onPick(opt.workflowActionId), className: "w-full text-left rounded-md border border-border px-3 py-2 text-xs hover:bg-accent", children: [_jsx("div", { className: "font-medium truncate", children: opt.name ?? 'Unnamed transfer' }), opt.description && (_jsx("div", { className: "text-muted-foreground truncate", children: opt.description })), opt.dialSummary && (_jsx("div", { className: "text-muted-foreground truncate font-mono text-[11px]", children: opt.dialSummary }))] }, `${opt.source}:${opt.workflowActionId}`))) })] }));
}
//# sourceMappingURL=TransferNodePanel.js.map